import { API_V2 } from "@/config/api";
import { RecurringPlan } from "@/types/models/RecurringPlan";
import { apiRequest } from "@gogive/services";

export const routes = {
  recurringPlansList: `${API_V2}/recurring`,
  recurringPlansStats: `${API_V2}/recurring/stats`,
  stopRecurringPlan: (id: number) => `${API_V2}/recurring/${id}/stop`,
  downloadReport: `${API_V2}/recurring/report`,
};

/** One row per donor: highest monthly-equivalent recurring amount among active plans. */
export interface TopRecurringDonor {
  id: number;
  firstName: string;
  lastName: string;
  monthlyAmountCents: number;
  /** Net amount to charity (gross - gogive fee), same basis as TopDonor.totalAmount */
  totalCents: number;
  planStartDate: string;
  planAgeDays: number;
}

/** One row per donor: earliest active plan for that donor. */
export interface OldestActiveDonor {
  id: number;
  firstName: string;
  lastName: string;
  monthlyAmountCents: number;
  /** Net amount to charity (gross - gogive fee), same basis as TopDonor.totalAmount */
  totalCents: number;
  planStartDate: string;
  planAgeDays: number;
}

export interface RecurringPlansStatsResponse {
  activePlansCount: number;
  monthlyRecurringTotalCents: number;

  newPlansThisMonth: number;
  cancelledPlansThisMonth: number;
  netPlanChange: number;

  lastMonthMrrCents: number;
  mrrChangePercent: number;
  /** Oldest → newest; last entry is current live MRR (same basis as monthlyRecurringTotalCents). */
  mrrTrendCents: number[];

  averageDonationCents: number;
  medianDonationCents: number;
  annualizedRevenueCents: number;
  totalRaisedCents: number;

  donorChurnRate: number;
  revenueChurnRate: number;
  averagePlanAgeDays: number;
  retention3Months: number;
  retention6Months: number;
  retention12Months: number;

  topRecurringDonors: TopRecurringDonor[];
  oldestActiveDonors: OldestActiveDonor[];
}

export type RecurringPlansListSortBy = "createdAt" | "grossAmount" | "total";

export interface RecurringPlansRequest {
  page: number;
  limit: number;
  sortBy: RecurringPlansListSortBy;
  sortDirection: "ASC" | "DESC";
  campaignId?: number;
  /** Backend: QueryRecurringPlans.planStatus */
  planStatus?: string;
  /** Backend: QueryRecurringPlans.planInterval */
  planInterval?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface ExtendedPlan extends RecurringPlan {
  donationCount: number;
  lastDonation: Date;
}

export interface RecurringPlansResponse {
  result: ExtendedPlan[];
  total: number;
  page: number;
  pages: number;
}

export const stopRecurringPlanRequest = (id: number) =>
  apiRequest<RecurringPlan>(routes.stopRecurringPlan(id), {
    method: "DELETE",
  });

export const downloadRecurringPlans = () =>
  apiRequest<any>(routes.downloadReport, {
    method: "GET",
    responseType: "blob",
  });
